import {Action} from 'redux'
import {Cmd, Loop, LoopReducer, loop} from 'redux-loop'

import {Actions} from '../actions'
import * as authActions from '../actions/auth'
import * as coreActions from '../actions/core'

import {isAdminModePermission} from '../libs/user'
import * as userApi from '../services/api/user'
import authService from '../services/auth'
import getInitialData from '../services/init-data'

import {ExtendedCognitoUser, ExtendedCognitoUserWithPermission} from '../types'

export type AuthState = {
  initDone: boolean
  user: ExtendedCognitoUserWithPermission | null
  isAdminMode: boolean
}

type AuthReducer<A extends Action = Actions> = (
  state: AuthState,
  action: A
) => AuthState | Loop<AuthState>

const initialState: AuthState = {
  initDone: false,
  user: null,
  isAdminMode: false,
}

const init: AuthReducer<coreActions.InitAction> = state =>
  loop(
    state,
    Cmd.list([
      Cmd.run(
        () =>
          authService
            .getAuth()
            .then((auth): Promise<ExtendedCognitoUser> => auth.currentAuthenticatedUser())
            .then((authenticatedUser: ExtendedCognitoUser) => {
              const userId = authenticatedUser.attributes.sub
              return userApi.getUserPermissions(userId).then(userPermissionResponse => {
                const newUser: ExtendedCognitoUserWithPermission = Object.assign(
                  authenticatedUser,
                  {permission: userPermissionResponse.permissions}
                )
                const providerId = userPermissionResponse.provider || undefined
                if (!providerId) {
                  // Triggers fail action creator which just ends initialisation and forces the user to sign in
                  throw new Error('Sign in failed (incorrect permissions after init)')
                }
                return {newUser, providerId}
              })
            }),
        {
          successActionCreator: ({newUser, providerId}) => authActions.signIn(newUser, providerId),
          failActionCreator: authActions.initDone,
        }
      ),
      Cmd.run(
        (dispatch, getState) => {
          authService.onAuthFailAfterVisibilityChange(() => {
            const currentState = getState()
            if (currentState.auth.user) {
              dispatch(authActions.signOut())
            }
          })
        },
        {
          args: [Cmd.dispatch, Cmd.getState],
        }
      ),
    ])
  )

const initDone: AuthReducer<authActions.InitDoneAction> = state => ({
  ...state,
  initDone: true,
})

const signIn: AuthReducer<authActions.SignInAction> = (state, action) => {
  const providerId = action.payload.providerId
  const signInUser = action.payload.user

  return loop(
    state,
    Cmd.run(
      async dispatch => {
        const initialData = await getInitialData(signInUser.permission, providerId)
        dispatch(coreActions.initialDataReceived(initialData))
        return signInUser
      },
      {
        args: [Cmd.dispatch],
        successActionCreator: authActions.signInDone,
        failActionCreator: coreActions.fatalError,
      }
    )
  )
}

const signInDone: AuthReducer<authActions.SignInDoneAction> = (state, action) => ({
  ...state,
  initDone: true,
  user: action.payload.user,
  isAdminMode: isAdminModePermission(action.payload.user.permission),
})

const signOut: AuthReducer<authActions.SignOutAction> = (state, action) =>
  loop(
    {
      ...state,
      user: null,
      isAdminMode: false,
    },
    Cmd.run(() => authService.getAuth().then(auth => auth.signOut({global: action.payload.global})))
  )

const reducer: LoopReducer<AuthState, Actions> = (state = initialState, action) => {
  switch (action.type) {
    case 'CORE.INIT':
      return init(state, action)
    case 'AUTH.INIT_DONE':
      return initDone(state, action)
    case 'AUTH.SIGN_IN':
      return signIn(state, action)
    case 'AUTH.SIGN_IN_DONE':
      return signInDone(state, action)
    case 'AUTH.SIGN_OUT':
      return signOut(state, action)
    default:
      return state
  }
}

export default reducer
